import { Schema } from "effect";

export const SWITCHBOARD_OUTCOMES = [
  "calling",
  "answered",
  "voicemail",
  "no_answer",
  "transferred",
  "transfer_failed",
  "failed",
] as const;
export type SwitchboardFilters = {
  dateStart?: string;
  dateEnd?: string;
  organizationId?: string;
  navigatorId?: string;
  provider?: string;
  outcome?: string;
  activity?: string;
  answerKind?: string;
  search?: string;
};
export const SwitchboardActivity = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  type: Schema.String,
  status: Schema.String,
});
export const SwitchboardCallPathValues = Schema.Struct({
  firstAnswerer: Schema.NullOr(Schema.String),
  screenerDecision: Schema.NullOr(Schema.String),
  handoffFailureReason: Schema.NullOr(Schema.String),
});
export const SwitchboardCallPathReview = Schema.Struct({
  recorded: Schema.NullOr(SwitchboardCallPathValues),
  reviewed: Schema.NullOr(SwitchboardCallPathValues),
  promptVersion: Schema.NullOr(Schema.Number),
  reviewedAt: Schema.NullOr(Schema.String),
  disagrees: Schema.Boolean,
});
export type SwitchboardCallPathReview = typeof SwitchboardCallPathReview.Type;
export const SwitchboardCallRow = Schema.Struct({
  id: Schema.String,
  sessionId: Schema.String,
  carespaceId: Schema.String,
  carespaceName: Schema.String,
  organizationId: Schema.String,
  organizationName: Schema.String,
  navigatorId: Schema.String,
  navigatorName: Schema.String,
  provider: Schema.String,
  providerCallId: Schema.NullOr(Schema.String),
  outcome: Schema.String,
  outcomeReason: Schema.NullOr(Schema.String),
  answerKind: Schema.String,
  voicemailDelivered: Schema.NullOr(Schema.Boolean),
  navigatorLed: Schema.Boolean,
  careCallConnected: Schema.Boolean,
  careCallCompleted: Schema.Boolean,
  timeSavedSeconds: Schema.NullOr(Schema.Number),
  callback: Schema.NullOr(Schema.String),
  pathReview: Schema.optionalKey(SwitchboardCallPathReview),
  dialedAt: Schema.String,
  finishedAt: Schema.NullOr(Schema.String),
  durationSeconds: Schema.NullOr(Schema.Number),
  activities: Schema.Array(SwitchboardActivity),
});
export type SwitchboardCallRow = typeof SwitchboardCallRow.Type;
export const SwitchboardFlow = Schema.Struct({
  provider: Schema.String,
  outcome: Schema.String,
  outcomeReason: Schema.optionalKey(Schema.NullOr(Schema.String)),
  answerKind: Schema.optionalKey(Schema.String),
  voicemailDelivered: Schema.optionalKey(Schema.NullOr(Schema.Boolean)),
  activity: Schema.String,
  callback: Schema.optional(Schema.NullOr(Schema.String)),
  count: Schema.Number,
});
export type SwitchboardFlow = typeof SwitchboardFlow.Type;
export const SwitchboardPathFlow = Schema.Struct({
  firstAnswerer: Schema.String,
  screenerDecision: Schema.String,
  handoffFailureReason: Schema.String,
  count: Schema.Number,
  disagreementCount: Schema.Number,
});
export type SwitchboardPathFlow = typeof SwitchboardPathFlow.Type;
export const SwitchboardPathTreeCount = Schema.Struct({
  node: Schema.String,
  count: Schema.Number,
});
export type SwitchboardPathTreeCount = typeof SwitchboardPathTreeCount.Type;
export const SwitchboardSnapshot = Schema.Struct({
  period: Schema.Struct({ dateStart: Schema.String, dateEnd: Schema.String }),
  impact: Schema.Struct({
    timeSavedSeconds: Schema.Number,
    timeSavedMeasuredCalls: Schema.Number,
    timeSavedEligibleCalls: Schema.Number,
    connectedCareCalls: Schema.Number,
    completedCareCalls: Schema.Number,
    activeNavigators: Schema.Number,
    switchboardCalls: Schema.Number,
    totalOutboundCalls: Schema.Number,
    switchboardShare: Schema.NullOr(Schema.Number),
  }),
  summary: Schema.Struct({
    totalCalls: Schema.Number,
    completedCalls: Schema.Number,
    transferredCalls: Schema.Number,
    voicemailCalls: Schema.Number,
    voicemailSystems: Schema.Number,
    callbackCalls: Schema.Number,
    failedCalls: Schema.Number,
    callsWithActivity: Schema.Number,
    latestUpdatedAt: Schema.NullOr(Schema.String),
  }),
  pathReviewSummary: Schema.optionalKey(
    Schema.Struct({
      reviewedCalls: Schema.Number,
      callsWithDisagreement: Schema.Number,
      latestReviewedAt: Schema.NullOr(Schema.String),
      promptVersions: Schema.Array(Schema.Number),
    }),
  ),
  calls: Schema.Array(SwitchboardCallRow),
  flows: Schema.Array(SwitchboardFlow),
  pathFlows: Schema.optionalKey(Schema.Array(SwitchboardPathFlow)),
  pathTreeCounts: Schema.optionalKey(Schema.Array(SwitchboardPathTreeCount)),
  organizations: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String })),
  navigators: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String })),
});
export const SwitchboardResponse = Schema.Struct({
  ...SwitchboardSnapshot.fields,
  generatedAt: Schema.String,
  timezone: Schema.String,
  page: Schema.Number,
  pageSize: Schema.Number,
});
export type SwitchboardResponse = typeof SwitchboardResponse.Type;

export const SwitchboardHealthEvent = Schema.Struct({
  id: Schema.String,
  type: Schema.String,
  occurredAt: Schema.String,
  receivedAt: Schema.String,
  receiptSeconds: Schema.Number,
});
export const SwitchboardHealthCall = Schema.Struct({
  id: Schema.String,
  nativeCallId: Schema.NullOr(Schema.String),
  carespaceId: Schema.String,
  carespaceName: Schema.String,
  navigatorName: Schema.String,
  dialedAt: Schema.String,
  outcome: Schema.String,
  nativeStatus: Schema.NullOr(Schema.String),
  roomName: Schema.NullOr(Schema.String),
  roomReady: Schema.NullOr(Schema.Boolean),
  dailySessionId: Schema.NullOr(Schema.String),
  dialStatus: Schema.NullOr(Schema.String),
  errorCode: Schema.NullOr(Schema.String),
  attemptedAt: Schema.NullOr(Schema.String),
  answeredAt: Schema.NullOr(Schema.String),
  endedAt: Schema.NullOr(Schema.String),
  handoffAt: Schema.NullOr(Schema.String),
  staffJoinedAt: Schema.NullOr(Schema.String),
  workerStatus: Schema.NullOr(Schema.String),
  workerErrors: Schema.Number,
  recordingStatus: Schema.NullOr(Schema.String),
  recordingReadyAt: Schema.NullOr(Schema.String),
  eventCount: Schema.Number,
  p95ReceiptSeconds: Schema.NullOr(Schema.Number),
  issues: Schema.Array(Schema.String),
  events: Schema.Array(SwitchboardHealthEvent),
});
export type SwitchboardHealthCall = typeof SwitchboardHealthCall.Type;
export const SwitchboardHealthSnapshot = Schema.Struct({
  period: Schema.Struct({ dateStart: Schema.String, dateEnd: Schema.String }),
  summary: Schema.Struct({
    totalAttempts: Schema.Number,
    nativeCalls: Schema.Number,
    reviewCalls: Schema.Number,
    eventExpectedCalls: Schema.Number,
    eventCoveredCalls: Schema.Number,
    eventGapCalls: Schema.Number,
    observedEvents: Schema.Number,
    delayedEvents: Schema.Number,
    clockSkewEvents: Schema.Number,
    p95ReceiptSeconds: Schema.NullOr(Schema.Number),
    latestEventReceivedAt: Schema.NullOr(Schema.String),
    dialAttempts: Schema.Number,
    dialFailures: Schema.Number,
    preparationFailures: Schema.Number,
    staleCalls: Schema.Number,
    workerErrorCalls: Schema.Number,
    recordingExpectedCalls: Schema.Number,
    recordingReadyCalls: Schema.Number,
    recordingFailureCalls: Schema.Number,
  }),
  daily: Schema.Array(
    Schema.Struct({
      date: Schema.String,
      attempts: Schema.Number,
      reviewCalls: Schema.Number,
      eventGaps: Schema.Number,
    }),
  ),
  failures: Schema.Array(
    Schema.Struct({ stage: Schema.String, code: Schema.String, calls: Schema.Number }),
  ),
  calls: Schema.Array(SwitchboardHealthCall),
});
export const SwitchboardHealthResponse = Schema.Struct({
  ...SwitchboardHealthSnapshot.fields,
  generatedAt: Schema.String,
  timezone: Schema.String,
  page: Schema.Number,
  pageSize: Schema.Number,
});
export type SwitchboardHealthResponse = typeof SwitchboardHealthResponse.Type;
